import { supabase } from '@/integrations/supabase/client'

// Rate limiting configuration
const RATE_LIMIT_DELAY = 100 // 100ms between requests
const MAX_RETRIES = 3

// Provider detection based on MX records
function detectProviderFromMX(mxRecords: string[]): string {
  if (!mxRecords || !Array.isArray(mxRecords)) {
    return 'OTHER'
  }
  
  const mxString = mxRecords.join(' ').toLowerCase()
  
  // Gmail detection (includes both regular Gmail and Google Workspace)
  if (mxString.includes('gmail-smtp-in.l.google.com') || 
      mxString.includes('alt1.gmail-smtp-in.l.google.com') ||
      mxString.includes('alt2.gmail-smtp-in.l.google.com') ||
      mxString.includes('alt3.gmail-smtp-in.l.google.com') ||
      mxString.includes('alt4.gmail-smtp-in.l.google.com') ||
      mxString.includes('aspmx.l.google.com') ||
      mxString.includes('alt1.aspmx.l.google.com') ||
      mxString.includes('alt2.aspmx.l.google.com') ||
      mxString.includes('alt3.aspmx.l.google.com') ||
      mxString.includes('alt4.aspmx.l.google.com') ||
      mxString.includes('aspmx2.googlemail.com') ||
      mxString.includes('aspmx3.googlemail.com')) {
    return 'GMAIL'
  }
  
  // Outlook/Hotmail detection
  if (mxString.includes('outlook.com') || 
      mxString.includes('hotmail.com') ||
      mxString.includes('live.com') ||
      mxString.includes('ms-acdc.office.com') ||
      mxString.includes('protection.outlook.com')) {
    return 'OUTLOOK'
  }
  
  // Everything else is OTHER (including Yahoo, Barracuda, custom servers, etc.)
  return 'OTHER'
}

// Extract domain from email
function extractDomain(email: string): string | null {
  if (!email || typeof email !== 'string') return null
  const parts = email.split('@')
  return parts.length === 2 ? parts[1].toLowerCase() : null
}

// Check MX records using Google DNS API
async function checkMXRecords(domain: string): Promise<string[]> {
  try {
    // Try multiple DNS services for better reliability
    const dnsServices = [
      `https://dns.google/resolve?name=${domain}&type=MX`,
      `https://cloudflare-dns.com/dns-query?name=${domain}&type=MX`,
      `https://1.1.1.1/dns-query?name=${domain}&type=MX`
    ]
    
    for (const url of dnsServices) {
      try {
        const response = await fetch(url, {
          headers: {
            'Accept': 'application/dns-json'
          }
        })
        
        if (!response.ok) {
          continue // Try next service
        }
        
        const data = await response.json()
        
        if (data.Status !== 0) {
          continue // Try next service
        }
        
        // Extract MX records
        const mxRecords: string[] = []
        if (data.Answer && Array.isArray(data.Answer)) {
          data.Answer.forEach((record: any) => {
            if (record.type === 15) { // MX record type
              mxRecords.push(record.data)
            }
          })
        }
        
        if (mxRecords.length > 0) {
          return mxRecords
        }
        
      } catch (serviceError) {
        console.warn(`⚠️ DNS service failed for ${domain}:`, serviceError)
        continue // Try next service
      }
    }
    
    // If all services fail, return empty array
    console.warn(`⚠️ All DNS services failed for ${domain}, using fallback`)
    return []
    
  } catch (error) {
    console.error(`❌ Error checking MX for ${domain}:`, error)
    return [] // Return empty array instead of throwing
  }
}

// Fallback provider detection based on common domain patterns
function detectProviderFromDomain(domain: string): string {
  const domainLower = domain.toLowerCase()
  
  // Common Gmail domains
  if (domainLower === 'gmail.com' || 
      domainLower === 'googlemail.com' ||
      domainLower.includes('google.com')) {
    return 'GMAIL'
  }
  
  // Common Outlook domains
  if (domainLower === 'outlook.com' || 
      domainLower === 'hotmail.com' ||
      domainLower === 'live.com' ||
      domainLower === 'msn.com' ||
      domainLower.includes('microsoft.com')) {
    return 'OUTLOOK'
  }
  
  // Common Yahoo domains
  if (domainLower === 'yahoo.com' || 
      domainLower === 'yahoo.co.uk' ||
      domainLower === 'ymail.com' ||
      domainLower === 'rocketmail.com') {
    return 'OTHER' // Yahoo is categorized as OTHER
  }
  
  // Everything else is OTHER
  return 'OTHER'
}

// Process a single email and detect provider
export async function processEmailProvider(email: string): Promise<string | null> {
  const domain = extractDomain(email)
  if (!domain) {
    return 'OTHER'
  }
  
  try {
    const mxRecords = await checkMXRecords(domain)
    
    let provider: string
    
    if (mxRecords.length > 0) {
      // Use MX records if available
      provider = detectProviderFromMX(mxRecords)
    } else {
      // Fallback to domain pattern matching
      provider = detectProviderFromDomain(domain)
      console.log(`⚠️ Using domain fallback for ${domain}: ${provider}`)
    }
    
    console.log(`✅ ${email} (${domain}) → ${provider}`)
    return provider
    
  } catch (error) {
    console.error(`❌ Failed to process ${email}:`, error)
    // Use domain fallback as last resort
    const fallbackProvider = detectProviderFromDomain(domain)
    console.log(`🔄 Using fallback for ${email}: ${fallbackProvider}`)
    return fallbackProvider
  }
}

// Process new email events (all types)
export async function processNewEmailEvents(): Promise<void> {
  try {
    console.log('🔄 Processing new email events for lead_email_provider...')
    
    // Get all email events that don't have lead_email_provider set
    const { data: emailEvents, error: fetchError } = await supabase
      .from('obsync_dynamic_reports')
      .select('id, to_email, lead_email_provider, event_type')
      .in('event_type', ['EMAIL_SENT', 'EMAIL_REPLY', 'EMAIL_BOUNCE'])
      .is('lead_email_provider', null)
      .not('to_email', 'is', null)
      .limit(100) // Process in batches of 100
    
    if (fetchError) {
      console.error('❌ Error fetching new email events:', fetchError)
      return
    }
    
    if (!emailEvents || emailEvents.length === 0) {
      console.log('✅ No new email events to process')
      return
    }
    
    console.log(`📊 Found ${emailEvents.length} new email events to process`)
    
    // Group by event type for logging
    const eventTypeCounts = emailEvents.reduce((acc, event) => {
      const eventType = (event as any).event_type || 'UNKNOWN'
      acc[eventType] = (acc[eventType] || 0) + 1
      return acc
    }, {} as Record<string, number>)
    
    console.log('📊 Event type breakdown:', eventTypeCounts)
    
    let successCount = 0
    let retryList: any[] = []
    
    // Process each event
    for (const event of emailEvents) {
      if (!event || typeof event !== 'object') continue
      
      const provider = await processEmailProvider((event as any).to_email)
      
      if (provider === null) {
        // Failed lookup - add to retry list
        retryList.push(event)
      } else {
        // Update the record
        const { error: updateError } = await supabase
          .from('obsync_dynamic_reports')
          .update({ lead_email_provider: provider } as any)
          .eq('id', (event as any).id)
        
        if (updateError) {
          console.error(`❌ Error updating record ${(event as any).id}:`, updateError)
        } else {
          successCount++
        }
      }
      
      // Rate limiting
      await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY))
    }
    
    console.log(`✅ Processed ${emailEvents.length} events (${successCount} successful)`)
    
    // Retry failed records
    if (retryList.length > 0) {
      console.log(`🔄 Retrying ${retryList.length} failed records...`)
      
      for (const event of retryList) {
        if (!event || typeof event !== 'object') continue
        
        const provider = await processEmailProvider((event as any).to_email)
        
        if (provider && provider !== null) {
          const { error: updateError } = await supabase
            .from('obsync_dynamic_reports')
            .update({ lead_email_provider: provider } as any)
            .eq('id', (event as any).id)
          
          if (!updateError) {
            successCount++
          }
        }
        
        await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY))
      }
    }
    
  } catch (error) {
    console.error('❌ Error processing new email events:', error)
  }
}

// Backfill lead email providers for a specific campaign by name
export async function backfillCampaignByName(campaignName: string): Promise<{
  success: boolean
  processed: number
  campaignId?: string
  variants?: any[]
  message: string
}> {
  try {
    console.log(`🚀 Starting backfill for campaign: ${campaignName}`)
    
    // Step 1: Find the campaign by name
    const { data: campaigns, error: campaignError } = await supabase
      .from('campaigns')
      .select('id, name')
      .ilike('name', `%${campaignName}%`)
    
    if (campaignError) {
      console.error('❌ Error finding campaign:', campaignError)
      return { success: false, processed: 0, message: `Error finding campaign: ${campaignError.message}` }
    }
    
    if (!campaigns || campaigns.length === 0) {
      return { success: false, processed: 0, message: `No campaign found with name containing "${campaignName}"` }
    }
    
    const campaign = campaigns[0]
    console.log(`🎯 Found campaign: ${campaign.name} (${campaign.id})`)
    
    // Step 2: Get all segments for this campaign
    const { data: segments, error: segmentsError } = await supabase
      .from('ab_test_segments')
      .select('id, name')
      .eq('campaign_id', campaign.id)
    
    if (segmentsError) {
      console.error('❌ Error fetching segments:', segmentsError)
      return { success: false, processed: 0, message: `Error fetching segments: ${segmentsError.message}` }
    }
    
    if (!segments || segments.length === 0) {
      return { success: false, processed: 0, message: 'No segments found for campaign' }
    }
    
    // Step 3: Get all variants for these segments
    const segmentIds = segments.map(s => s.id)
    const { data: variants, error: variantsError } = await supabase
      .from('ab_test_variants')
      .select('id, name, smartlead_campaign_id')
      .in('segment_id', segmentIds)
    
    if (variantsError) {
      console.error('❌ Error fetching variants:', variantsError)
      return { success: false, processed: 0, message: `Error fetching variants: ${variantsError.message}` }
    }
    
    if (!variants || variants.length === 0) {
      return { success: false, processed: 0, message: 'No variants found for segments' }
    }
    
    console.log(`📊 Found ${variants.length} variants for campaign`)
    
    // Step 4: Get all smartlead campaign IDs
    const smartleadCampaignIds = variants
      .map(v => v.smartlead_campaign_id)
      .filter(id => id !== null && id !== undefined)
      .map(id => parseInt(id, 10))
    
    console.log(`📈 Smartlead Campaign IDs: ${smartleadCampaignIds.join(', ')}`)
    
    if (smartleadCampaignIds.length === 0) {
      return { success: false, processed: 0, message: 'No smartlead campaign IDs found' }
    }
    
    // Step 5: Get all email events that need lead_email_provider
    const { data: emailEvents, error: eventsError } = await supabase
      .from('obsync_dynamic_reports')
      .select('id, to_email, event_type, campaign_id')
      .in('campaign_id', smartleadCampaignIds)
      .in('event_type', ['EMAIL_SENT', 'EMAIL_REPLY', 'EMAIL_BOUNCE'])
      .is('lead_email_provider', null)
      .not('to_email', 'is', null)
    
    if (eventsError) {
      console.error('❌ Error fetching email events:', eventsError)
      return { success: false, processed: 0, message: `Error fetching email events: ${eventsError.message}` }
    }
    
    console.log(`📧 Found ${emailEvents?.length || 0} email events to process`)
    
    if (!emailEvents || emailEvents.length === 0) {
      return { 
        success: true, 
        processed: 0, 
        campaignId: campaign.id,
        variants: variants,
        message: 'No email events need processing - all already have lead_email_provider' 
      }
    }
    
    // Step 6: Process events
    let processed = 0
    let success = 0
    let failed = 0
    
    for (const event of emailEvents) {
      try {
        const provider = await processEmailProvider((event as any).to_email)
        
        if (provider && provider !== null) {
          const { error: updateError } = await supabase
            .from('obsync_dynamic_reports')
            .update({ lead_email_provider: provider } as any)
            .eq('id', (event as any).id)
          
          if (updateError) {
            console.error(`❌ Error updating record ${(event as any).id}:`, updateError)
            failed++
          } else {
            success++
          }
        } else {
          failed++
        }
        
        processed++
        
        // Rate limiting
        await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY))
        
        // Progress update every 10 records
        if (processed % 10 === 0) {
          console.log(`📊 Progress: ${processed}/${emailEvents.length} (${success} success, ${failed} failed)`)
        }
        
      } catch (error) {
        console.error(`❌ Error processing event ${(event as any).id}:`, error)
        failed++
        processed++
      }
    }
    
    console.log(`✅ Backfill completed: ${success} successful, ${failed} failed`)
    
    return {
      success: true,
      processed: success,
      campaignId: campaign.id,
      variants: variants,
      message: `Processed ${processed} events (${success} successful, ${failed} failed)`
    }
    
  } catch (error) {
    console.error('❌ Fatal error during backfill:', error)
    return { success: false, processed: 0, message: `Fatal error: ${error.message}` }
  }
}

// Get lead email provider statistics
export async function getLeadEmailProviderStats(campaignId?: number): Promise<{
  total: number
  gmail: number
  outlook: number
  other: number
  unknown: number
}> {
  try {
    let query = supabase
      .from('obsync_dynamic_reports')
      .select('lead_email_provider')
      .eq('event_type', 'EMAIL_SENT')
      .not('to_email', 'is', null)
    
    if (campaignId) {
      query = query.eq('campaign_id', campaignId)
    }
    
    const { data, error } = await query
    
    if (error) {
      console.error('❌ Error fetching lead email provider stats:', error)
      return { total: 0, gmail: 0, outlook: 0, other: 0, unknown: 0 }
    }
    
    const stats = {
      total: data?.length || 0,
      gmail: data?.filter((d: any) => d?.lead_email_provider === 'GMAIL').length || 0,
      outlook: data?.filter((d: any) => d?.lead_email_provider === 'OUTLOOK').length || 0,
      other: data?.filter((d: any) => d?.lead_email_provider === 'OTHER').length || 0,
      unknown: data?.filter((d: any) => !d?.lead_email_provider).length || 0
    }
    
    return stats
    
  } catch (error) {
    console.error('❌ Error getting lead email provider stats:', error)
    return { total: 0, gmail: 0, outlook: 0, other: 0, unknown: 0 }
  }
}
